import { useMsal } from '@azure/msal-react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { axisBottom, axisLeft, line, pointer, scaleLinear, scaleTime, select, timeFormat } from 'd3';

import { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { AccountTree, Apps, Architecture, ArrowForward, Cloud, DataObject, Event, HexagonOutlined, Layers, QueryStats, Refresh, ViewQuilt } from '@mui/icons-material';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Divider, Fade, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { DashboardClient, DashboardOperationalMetricPointModel, DashboardOverviewModel } from '../../api/api';
import { useApiClient } from '../../common';
import { Permissions } from '../../common/Permissions';
import { formatTimestampRelative } from '../../common/formatTimestamp';
import useHasPermission from '../../common/hooks/useHasPermission';
import AuthContext from '../../components/AuthContext';
import { useChangeSet } from '../../components/ChangeSetContext';
import PromptChat from '../../components/Chat/PromptChat';
import { useHomeChatAdapter } from './useHomeChatAdapter';

type DashboardCardProps = {
	title: string;
	description: string;
	icon: React.ReactNode;
	color: string;
	items: Array<{ label: string; value: number }>;
	to: string;
	linkLabel: string;
};

function DashboardCard({ title, description, icon, color, items, to, linkLabel }: DashboardCardProps) {
	return (
		<Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
			<CardContent sx={{ p: 2.25, '&:last-child': { pb: 2 } }}>
				<Stack direction="row" spacing={1.25} alignItems="flex-start">
					<Box sx={{ display: 'grid', placeItems: 'center', width: 40, height: 40, borderRadius: 1.5, color, bgcolor: alpha(color, 0.12), flexShrink: 0 }}>{icon}</Box>
					<Box sx={{ minWidth: 0 }}>
						<Typography component="h2" variant="subtitle1" fontWeight={700}>{title}</Typography>
						<Typography variant="caption" color="text.secondary">{description}</Typography>
					</Box>
				</Stack>
				<Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(items.length, 5)}, minmax(0, 1fr))`, mt: 2 }}>
					{items.map((item, index) => (
						<Box key={item.label} sx={{ pl: index === 0 ? 0 : 0.75, borderLeft: index === 0 ? 0 : 1, borderColor: 'divider', minWidth: 0 }}>
							<Typography variant="h6" fontWeight={700}>{item.value.toLocaleString()}</Typography>
							<Typography variant="caption" color="text.secondary" noWrap title={item.label} sx={{ fontSize: '0.625rem', lineHeight: 1, letterSpacing: '-0.015em' }}>{item.label}</Typography>
						</Box>
					))}
				</Box>
				<Button component={Link} to={to} size="small" endIcon={<ArrowForward />} sx={{ mt: 1.75, px: 0 }}>{linkLabel}</Button>
			</CardContent>
		</Card>
	);
}

function DashboardSkeleton() {
	return (
		<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
			{[0, 1, 2].map((item) => <Skeleton key={item} variant="rounded" height={190} />)}
		</Box>
	);
}

function OverviewCards({ data }: { data: DashboardOverviewModel }) {
	const theme = useTheme();
	return (
		<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
			<DashboardCard
				title="Apps"
				description="Apps and the UI artifacts they contain."
				icon={<ViewQuilt />}
				color={theme.palette.info.main}
				items={[
					{ label: 'Apps', value: data.experiences.apps },
					{ label: 'Views', value: data.experiences.views },
					{ label: 'Components', value: data.experiences.components },
				]}
				to="/app"
				linkLabel="Explore apps"
			/>
			<DashboardCard
				title="Services"
				description="Your business model and service boundaries."
				icon={<HexagonOutlined />}
				color={theme.palette.primary.main}
				items={[
					{ label: 'Subdomains', value: data.architecture.subdomains },
					{ label: 'Contexts', value: data.architecture.contexts },
					{ label: 'Entities', value: data.architecture.entities },
					{ label: 'Operations', value: data.architecture.operations },
				]}
				to="/service"
				linkLabel="Explore services"
			/>
			<DashboardCard
				title="Orchestration"
				description="Design and automate your business flows."
				icon={<AccountTree />}
				color={theme.palette.primary.main}
				items={[
					{ label: 'Flows', value: data.automation.flows },
					{ label: 'Agents', value: data.automation.agents },
					{ label: 'Operations', value: data.automation.operations },
				]}
				to="/flow"
				linkLabel="Explore orchestration"
			/>
		</Box>
	);
}

type OperationalMetricsChartProps = {
	points: DashboardOperationalMetricPointModel[];
	flowRunsColor: string;
	onHoverPoint: (point?: DashboardOperationalMetricPointModel) => void;
};

function OperationalMetricsChart({ points, flowRunsColor, onHoverPoint }: OperationalMetricsChartProps) {
	const svgRef = useRef<SVGSVGElement>(null);

	useEffect(() => {
		if (!svgRef.current || points.length < 2) {
			return;
		}

		const width = 720;
		const height = 176;
		const margin = { top: 8, right: 12, bottom: 28, left: 38 };
		const chartWidth = width - margin.left - margin.right;
		const chartHeight = height - margin.top - margin.bottom;
		const dates = points.map((point) => new Date(point.at));
		const firstDate = dates[0];
		const lastDate = dates[dates.length - 1];
		const xDomain = firstDate.getTime() === lastDate.getTime()
			? [new Date(firstDate.getTime() - 3_600_000), new Date(firstDate.getTime() + 3_600_000)]
			: [firstDate, lastDate];
		const maximum = Math.max(1, ...points.flatMap((point) => [point.flowRuns, point.webhookInvocations, point.operationCalls]));
		const x = scaleTime().domain(xDomain).range([0, chartWidth]);
		const y = scaleLinear().domain([0, maximum]).nice().range([chartHeight, 0]);
		const formatHour = timeFormat('%H:%M');
		const horizontalAxis = axisBottom(x)
			.ticks(Math.min(points.length, 6))
			.tickFormat((value) => formatHour(new Date(value.valueOf())));
		const svg = select(svgRef.current);
		svg.selectAll('*').remove();

		const chart = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);
		chart.append('g')
			.attr('transform', `translate(0,${chartHeight})`)
			.call(horizontalAxis)
			.call((axis) => axis.selectAll('text').attr('fill', 'currentColor').attr('font-size', 10))
			.call((axis) => axis.selectAll('path,line').attr('stroke', 'currentColor').attr('opacity', 0.28));
		chart.append('g')
			.call(axisLeft(y).ticks(4).tickSize(-chartWidth))
			.call((axis) => axis.selectAll('text').attr('fill', 'currentColor').attr('font-size', 10))
			.call((axis) => axis.selectAll('path,line').attr('stroke', 'currentColor').attr('opacity', 0.16));

		const series = [
			{ color: flowRunsColor, value: (point: DashboardOperationalMetricPointModel) => point.flowRuns },
			{ color: '#7e57c2', value: (point: DashboardOperationalMetricPointModel) => point.operationCalls },
			{ color: '#2e7d32', value: (point: DashboardOperationalMetricPointModel) => point.webhookInvocations },
		];
		for (const metric of series) {
			chart.append('path')
				.datum(points)
				.attr('fill', 'none')
				.attr('stroke', metric.color)
				.attr('stroke-width', 2)
				.attr('vector-effect', 'non-scaling-stroke')
				.attr('d', line<DashboardOperationalMetricPointModel>()
					.x((point) => x(new Date(point.at)))
					.y((point) => y(metric.value(point))) ?? '');
		}

		const focus = chart.append('g').style('display', 'none');
		focus.append('line')
			.attr('y1', 0)
			.attr('y2', chartHeight)
			.attr('stroke', 'currentColor')
			.attr('stroke-dasharray', '3 3')
			.attr('opacity', 0.4);
		const focusDots = series.map((metric) => focus.append('circle')
			.attr('r', 4)
			.attr('fill', metric.color)
			.attr('stroke', 'white')
			.attr('stroke-width', 1.5));

		chart.append('rect')
			.attr('width', chartWidth)
			.attr('height', chartHeight)
			.attr('fill', 'transparent')
			.style('cursor', 'crosshair')
			.on('mousemove', (event) => {
				const [mouseX] = pointer(event, svgRef.current);
				const hoveredAt = x.invert(Math.max(0, Math.min(chartWidth, mouseX - margin.left))).getTime();
				const hoveredPoint = points.reduce((closest, point) => Math.abs(new Date(point.at).getTime() - hoveredAt) < Math.abs(new Date(closest.at).getTime() - hoveredAt) ? point : closest);
				const pointX = x(new Date(hoveredPoint.at));
				focus.style('display', null);
				focus.select('line').attr('transform', `translate(${pointX},0)`);
				focusDots.forEach((dot, index) => dot.attr('cx', pointX).attr('cy', y(series[index].value(hoveredPoint))));
				onHoverPoint(hoveredPoint);
			})
			.on('mouseleave', () => {
				focus.style('display', 'none');
				onHoverPoint(undefined);
			});
	}, [flowRunsColor, onHoverPoint, points]);

	return <Box sx={{ height: 176, color: 'text.secondary' }}>
		<svg ref={svgRef} viewBox="0 0 720 176" preserveAspectRatio="none" width="100%" height="100%" aria-label="Flow runs, operation calls, and webhook intakes over the last 24 hours" />
	</Box>;
}

function OperationalMetrics({ data, onRefresh, isRefreshing }: { data: DashboardOverviewModel; onRefresh?: () => void; isRefreshing: boolean }) {
	const theme = useTheme();
	const [hoveredPoint, setHoveredPoint] = useState<DashboardOperationalMetricPointModel>();
	const metrics = data.metrics;
	const chartPoints = metrics.points ?? [];
	const formatDuration = (duration?: number) => duration == null
		? '—'
		: duration >= 1000
			? `${(duration / 1000).toFixed(1)} s`
			: `${Math.round(duration)} ms`;
	const formatDurationSummary = (average?: number, minimum?: number, maximum?: number) => {
		const averageValue = average != null ? `Avg ${formatDuration(average)}` : '—';
		const rangeValue = minimum != null && maximum != null ? `${formatDuration(minimum)} – ${formatDuration(maximum)}` : '—';
		return <>
			<Box component="span" sx={{ display: 'block', whiteSpace: 'nowrap' }}>{averageValue}</Box>
			<Box component="span" sx={{ display: 'block', whiteSpace: 'nowrap' }}>{rangeValue}</Box>
		</>;
	};

	return (
		<Card variant="outlined" sx={{ borderRadius: 2 }}>
			<CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
				<Stack direction="row" spacing={1.25} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
					<Stack direction="row" spacing={1.25} alignItems="center">
						<Box sx={{ display: 'grid', placeItems: 'center', width: 40, height: 40, borderRadius: 1.5, color: 'primary.main', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12) }}><QueryStats /></Box>
						<Box>
							<Typography component="h2" variant="subtitle1" fontWeight={700}>Operational metrics</Typography>
							<Typography variant="caption" color="text.secondary">Last 24 hours · Flow executions, operation calls, and service-connection webhooks.</Typography>
						</Box>
					</Stack>
					{onRefresh && <Button variant="outlined" size="small" startIcon={isRefreshing ? <CircularProgress size={14} /> : <Refresh />} onClick={onRefresh} disabled={isRefreshing} sx={{ flexShrink: 0 }}>
						Refresh 24 hours
					</Button>}
				</Stack>
				<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, columnGap: 3 }}>
					<Box sx={{ pr: { md: 3 }, borderRight: { md: 1 }, borderColor: 'divider' }}>
					<Typography variant="caption" color="text.secondary">Flow runs &amp; duration</Typography>
					<Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, mt: 0.5 }}>
					{[
						{ label: 'Runs', value: metrics.flowRuns.toLocaleString() },
						{ label: 'Failed', value: metrics.failedFlowRuns.toLocaleString() },
						{ label: 'Duration', value: formatDurationSummary(metrics.averageFlowDurationMs, metrics.minimumFlowDurationMs, metrics.maximumFlowDurationMs) },
						{ label: 'Webhook intakes', value: metrics.webhookInvocations.toLocaleString() },
					].map((metric, index) => (
						<Box key={metric.label} sx={{ px: { xs: index % 2 === 0 ? 0 : 1.5, md: index === 0 ? 0 : 2 }, py: { xs: index < 2 ? 1 : 0, md: 0 }, borderLeft: { xs: index % 2 === 0 ? 0 : 1, md: index === 0 ? 0 : 1 }, borderTop: { xs: index < 2 ? 0 : 1, md: 0 }, borderColor: 'divider' }}>
							<Typography variant="body2" fontWeight={700}>{metric.value}</Typography>
							<Typography variant="caption" color="text.secondary">{metric.label}</Typography>
						</Box>
					))}
					</Box>
					</Box>
					<Box sx={{ mt: { xs: 2, md: 0 }, pt: { xs: 1.5, md: 0 }, borderTop: { xs: 1, md: 0 }, borderColor: 'divider' }}>
					<Typography variant="caption" color="text.secondary">Operation calls &amp; duration</Typography>
					<Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, mt: 0.5 }}>
						{[
							{ label: 'Calls', value: metrics.operationCalls.toLocaleString() },
							{ label: 'Failed', value: metrics.failedOperationCalls.toLocaleString() },
							{ label: 'Duration', value: formatDurationSummary(metrics.averageOperationDurationMs, metrics.minimumOperationDurationMs, metrics.maximumOperationDurationMs) },
						].map((metric, index) => (
							<Box key={metric.label} sx={{ px: { xs: index % 2 === 0 ? 0 : 1.5, md: index === 0 ? 0 : 2 }, py: { xs: index < 2 ? 1 : 0, md: 0 }, borderLeft: { xs: index % 2 === 0 ? 0 : 1, md: index === 0 ? 0 : 1 }, borderTop: { xs: index < 2 ? 0 : 1, md: 0 }, borderColor: 'divider' }}>
								<Typography variant="body2" fontWeight={700}>{metric.value}</Typography>
								<Typography variant="caption" color="text.secondary">{metric.label}</Typography>
							</Box>
						))}
					</Box>
					</Box>
				</Box>
				{chartPoints.length > 1 && <Box sx={{ mt: 2 }}>
					<OperationalMetricsChart points={chartPoints} flowRunsColor={theme.palette.primary.main} onHoverPoint={setHoveredPoint} />
					<Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
						<Typography variant="caption" color="primary.main" sx={{ minWidth: 130, whiteSpace: 'nowrap' }}>● Flow runs{hoveredPoint ? `: ${hoveredPoint.flowRuns.toLocaleString()}` : ''}</Typography>
						<Typography variant="caption" sx={{ color: '#7e57c2', minWidth: 155, whiteSpace: 'nowrap' }}>● Operation calls{hoveredPoint ? `: ${hoveredPoint.operationCalls.toLocaleString()}` : ''}</Typography>
						<Typography variant="caption" sx={{ color: '#2e7d32', minWidth: 165, whiteSpace: 'nowrap' }}>● Webhook intakes{hoveredPoint ? `: ${hoveredPoint.webhookInvocations.toLocaleString()}` : ''}</Typography>
					</Stack>
				</Box>}
			</CardContent>
		</Card>
	);
}

export default function HomeView() {
	const { accounts } = useMsal();
	const auth = useContext(AuthContext);
	const { currentChangeSetId, availableChangeSets } = useChangeSet();
	const dashboardClient = useApiClient(DashboardClient);
	const canReadDashboard = useHasPermission(Permissions.DashboardRead);
	const canRefreshDashboard = useHasPermission(Permissions.DashboardRefresh);
	const chatAdapter = useHomeChatAdapter(auth.isAuthenticated);
	const userName = accounts[0]?.name?.split(' ')[0] || 'there';
	const currentChangeSet = availableChangeSets.find((changeSet) => changeSet.id === currentChangeSetId);

	const { data: dashboardData, isLoading, error, refetch, isFetching } = useQuery({
		queryKey: ['dashboard', 'overview', currentChangeSetId],
		queryFn: () => dashboardClient.getOverview(currentChangeSetId ?? undefined),
		enabled: auth.isAuthenticated && canReadDashboard,
		staleTime: 60_000,
	});
	const data = canReadDashboard ? dashboardData : undefined;
	const refreshSnapshotMutation = useMutation({
		mutationFn: () => dashboardClient.refresh(),
		onSuccess: () => {
			window.setTimeout(() => void refetch(), 2_000);
		},
	});

	if (!auth.isAuthenticated) {
		return (
			<Fade in timeout={600}>
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minHeight: 0, overflow: 'hidden', px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 } }}>
					<Box sx={{ width: '100%', maxWidth: 1040, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 2 }}>
						<Stack direction="row" spacing={1.5} alignItems="center">
							<Box component="img" src="/assets/octopus.png" alt="Randol" sx={{ width: 64, height: 64, objectFit: 'contain' }} />
							<Typography component="h1" variant="h5" fontWeight={600}>Hello {userName}, what would you like to safely vibe today?</Typography>
						</Stack>
						<Box sx={{ display: 'flex', flex: 1, minHeight: 0, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
							<PromptChat adapter={chatAdapter} defaultTitle="Randol Architect Assistant" headerIcon={<Architecture fontSize="small" color="primary" />} showConversationNameBar emptyStateText="Ask Randol to help you get moving." unavailableText="Sign in to start a conversation with Randol." />
						</Box>
					</Box>
				</Box>
			</Fade>
		);
	}

	return (
		<Fade in timeout={400}>
			<Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: { xs: 1.5, md: 2.5 } }}>
				<Stack spacing={1.75} sx={{ width: '100%', maxWidth: 1500, mx: 'auto' }}>
					<Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
						<Box>
							<Typography component="h1" variant="h4" fontWeight={700}>Good to see you, {userName}</Typography>
							<Typography color="text.secondary">Here&apos;s what&apos;s happening across your Randol environment{currentChangeSet ? ` in ${currentChangeSet.name}` : ''}.</Typography>
						</Box>
						{canReadDashboard && <Tooltip title="Refresh overview"><span><IconButton onClick={() => void refetch()} disabled={isFetching}>{isFetching ? <CircularProgress size={20} /> : <Refresh />}</IconButton></span></Tooltip>}
					</Stack>

					{error && <Alert severity="warning">The overview could not be refreshed. Randol chat is still available below.</Alert>}
					{refreshSnapshotMutation.isError && <Alert severity="warning">The metrics snapshot could not be queued. Please try again.</Alert>}
					{isLoading ? <DashboardSkeleton /> : data ? <><OverviewCards data={data} /><OperationalMetrics data={data} onRefresh={canRefreshDashboard ? () => refreshSnapshotMutation.mutate() : undefined} isRefreshing={refreshSnapshotMutation.isPending} /></> : null}

					<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(320px, 0.8fr) minmax(480px, 1.2fr)' }, gap: 1.5, alignItems: 'stretch' }}>
						<Card variant="outlined" sx={{ borderRadius: 2 }}>
							<CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
								<Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 2, py: 1.5 }}>
									<Event color="primary" />
									<Box sx={{ flex: 1 }}>
										<Typography component="h2" variant="subtitle1" fontWeight={700}>Recently updated</Typography>
										<Typography variant="caption" color="text.secondary">The latest changes across your artifacts.</Typography>
									</Box>
								</Stack>
								<Divider />
								{data?.recentArtifacts.length ? data.recentArtifacts.map((artifact, index) => (
									<Box key={`${artifact.type}-${artifact.id}`}>
										<Button component={Link} to={artifact.url} color="inherit" fullWidth sx={{ justifyContent: 'flex-start', textAlign: 'left', borderRadius: 0, px: 2, py: 1.15, textTransform: 'none' }}>
											<Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
											<Box sx={{ color: 'primary.main', display: 'grid', placeItems: 'center' }}>{artifact.type === 'App' ? <ViewQuilt fontSize="small" /> : artifact.type === 'Flow' ? <AccountTree fontSize="small" /> : artifact.type === 'Environment' ? <Cloud fontSize="small" /> : <Layers fontSize="small" />}</Box>
											<Box sx={{ minWidth: 0, flex: 1 }}>
												<Typography variant="body2" fontWeight={600} noWrap>{artifact.name}</Typography>
												<Typography variant="caption" color="text.secondary" noWrap>{artifact.type}</Typography>
											</Box>
											<Stack alignItems="flex-end" spacing={0.25} sx={{ flexShrink: 0, minWidth: 0 }}>
												{artifact.updatedByName && <Typography variant="caption" color="text.secondary" noWrap title={artifact.updatedByName}>{artifact.updatedByName}</Typography>}
												<Typography variant="caption" color="text.secondary" noWrap>{formatTimestampRelative(artifact.updatedAt)}</Typography>
											</Stack>
											</Stack>
										</Button>
										{index < data.recentArtifacts.length - 1 && <Divider />}
									</Box>
								)) : <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3 }}>No recent artifacts yet.</Typography>}
							</CardContent>
						</Card>

						<Card variant="outlined" sx={{ borderRadius: 2, minHeight: 440, overflow: 'hidden', display: 'flex' }}>
							<Box sx={{ display: 'flex', minHeight: 0, flex: 1 }}>
								<PromptChat adapter={chatAdapter} defaultTitle="Randol Architect Assistant" headerIcon={<Architecture fontSize="small" color="primary" />} showConversationNameBar area="Home" emptyStateText="What shall we build?" suggestedPrompts={[
									'Help me create a new app',
									'Design a flow to automate a process',
									'Help me model a new bounded context',
									'Explain the architecture in this environment',
								]} />
							</Box>
						</Card>
					</Box>

					{data && <Stack direction="row" spacing={1} alignItems="center" color="text.secondary" sx={{ px: 0.5 }}><DataObject sx={{ fontSize: 15 }} /><Typography variant="caption">Live inventory · Updated {formatTimestampRelative(data.generatedAt)}</Typography></Stack>}
				</Stack>
			</Box>
		</Fade>
	);
}
